import { useDataGridPagination } from '@hooks/useDataGridPagination'
import { useErrorMessage } from '@hooks/useErrorMessage'
import {
  GridColDef,
  GridFilterModel,
  GridInitialState,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-pro'
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog'
import { DefaultSnackbar } from '@shared/components/ui/DefaultSnackbar'
import {
  TrainingCompaniesQuery,
  TrainingCompaniesTraineesQuery,
  useConfirmTraineeMutation,
  useDisconnectTraineeMutation,
  useDisconnectTraineesMutation,
  useTrainingCompaniesTraineesQuery,
  useMyTraineesQuery,
  MyTraineesQuery,
  InviteTraineeInput,
  useInviteOrCreateTraineeMutation
} from '@typings/graphql'
import { Roles } from '@typings/roles'
import { isRole } from '@utils/permissions'
import React from 'react'
import {
  Trans,
  useTranslation
} from 'react-i18next'

const PAGE_SIZE = 50

// The grid's "profession" column filters/displays the profession id, but
// that's `professionId` on the User model server-side - map the grid's
// field name to the real filterable field before sending it to the API.
const GRID_FIELD_TO_API_FIELD: Record<string, string> = {
  profession: 'professionId'
}

type Props = React.PropsWithChildren & {
  disableFetching?: boolean
}

type TrainingCompany = Exclude<TrainingCompaniesQuery['trainingCompanies'], null | undefined>[0]

// This grid only ever needs the small set of fields rendered by
// traineesDataGridColumns. It's typed as a union of both source queries
// (an instructor's own trainees vs. a training company's full trainee list)
// rather than tying the row model to either query's full field set, since
// those two queries intentionally fetch different amounts of data.
type MyTrainee = Exclude<MyTraineesQuery['me']['trainees'], null | undefined>[0]
type TrainingCompanyTrainee = Exclude<Exclude<TrainingCompaniesTraineesQuery['trainingCompanies'], null | undefined>[0]['trainees']['edges'], null | undefined>[0]['node']

export type TraineeType = (MyTrainee | TrainingCompanyTrainee) & {
  name?: TrainingCompany['name']
}
export type TraineesRowModel = Omit<TraineeType, '__typename'>

export type TraineeColumnType = Omit<GridColDef<TraineesRowModel>, 'field'> & {
  field: keyof TraineesRowModel | 'action',
  hideHeader?: boolean,
}

export type TraineesProviderContextType = {
  selectedIds: GridRowSelectionModel
  selectedItem: TraineesRowModel | null
  traineeData: TraineesRowModel[]
  loading: boolean
  newTrainee: boolean
  // Server-side paging/sorting/filtering only applies to the
  // training-company branch (the one that can have hundreds of trainees) -
  // an instructor's own trainee list stays fully client-side since it's
  // small and already loaded in one shot.
  paginationMode: 'client' | 'server'
  paginationModel?: GridPaginationModel
  sortModel?: GridSortModel
  filterModel?: GridFilterModel
  rowCount: number
  updatePaginationModel: (model: GridPaginationModel) => void
  updateSortModel: (model: GridSortModel) => void
  updateFilterModel: (filter: GridFilterModel | undefined) => void
  updateInitialState: (initialState?: GridInitialState) => void
  handleInviteUser: (input: InviteTraineeInput, callback?: () => void) => void
  handleVerifyUser: (item: TraineesRowModel) => void
  handleDisconnect: (multiple: boolean, callback?: () => void) => void
  updateSelectedItem: (row: TraineesRowModel | null) => void
  updateNewTrainee: (value: boolean) => void
  updateSelectedIds: (ids: GridRowSelectionModel) => void
}

const TraineesProviderContext = React.createContext<TraineesProviderContextType>(
  {} as any
)

export const TraineesProvider:React.FC<Props> = ({ children }) => {
  const { t } = useTranslation()
  const [artificialLoading, setArtificialLoading] = React.useState(true)
  const [selectedIds, setSelectedIds] = React.useState<GridRowSelectionModel>([])
  const [showDisconnectDialog, setShowDisconnectDialog] = React.useState(false)
  const [showVerifyDialog, setShowVerifyDialog] = React.useState(false)
  const [isDisconnectLoading, setIsDisconnectLoading] = React.useState(false)
  const [isVerifyLoading, setIsVerifyLoading] = React.useState(false)
  const [selectedItem, setClickedRow] = React.useState<TraineesRowModel | null>(null)
  const [disconnectMany, setDisconnectMany] = React.useState<Boolean>(false)
  const [showToast, setShowToast] = React.useState(false)
  const [successMessage, setSuccessMessage] = React.useState('')
  const [newTrainee, setNewTrainee] = React.useState(false)

  const { errorMessage, setMessageByCode, setErrorMessage } = useErrorMessage()

  const [inviteTrainee] = useInviteOrCreateTraineeMutation()

  const isTrainingCompany = React.useMemo<boolean>(() => {
    return isRole(Roles.TRAINING_COMPANY_MANAGER)
  }, [])

  const {
    fetchVariablesRef, filterModel, paginationModel, sortModel,
    setFilterModelState, setPaginationModelState, setSortModelState, setInitialState
  } = useDataGridPagination<TrainingCompaniesTraineesQuery>()

  const [loadMore, setLoadMore] = React.useState(false)

  const { data, loading: dataLoading, refetch } = useTrainingCompaniesTraineesQuery({
    variables: {
      all: true,
      first: PAGE_SIZE
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !isTrainingCompany
  })

  const handleFetchMore = React.useCallback(async () => {
    setLoadMore(true)
    try {
      await refetch(fetchVariablesRef.current)
    } finally {
      setLoadMore(false)
    }
  }, [])

  // useDataGridPagination defaults to DATA_GRID_PAGE_SIZE (10) - this grid
  // wants 50 as its default, so seed it once up front.
  React.useEffect(() => {
    if (isTrainingCompany) {
      setInitialState({ pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } } })
    }
  }, [])

  const { data: traineesData, loading: traineesLoading } = useMyTraineesQuery({
    fetchPolicy: 'cache-and-network',
    skip: isTrainingCompany
  })

  const [disconnectTrainees] = useDisconnectTraineesMutation()
  const [disconnectTrainee] = useDisconnectTraineeMutation()
  const [confirmTrainee] = useConfirmTraineeMutation()

  const deleteCallbackRef = React.useRef<() => void>()

  React.useEffect(() => {
    setTimeout(() => {
      setArtificialLoading(false)
    }, 500)
  }, [])

  const loading = React.useMemo<boolean>(() => {
    return artificialLoading || dataLoading || traineesLoading || loadMore
  }, [artificialLoading, dataLoading, traineesLoading, loadMore])

  const updateSelectedIds = React.useCallback((ids: GridRowSelectionModel) => {
    setSelectedIds(ids)
  }, [])

  const updateSelectedItem = React.useCallback((row: TraineesRowModel | null) => {
    setClickedRow(row)
  }, [])

  const handleDisconnect = React.useCallback((multiple: Boolean, callback?: () => void) => {
    setDisconnectMany(multiple)
    setShowDisconnectDialog(true)
    deleteCallbackRef.current = callback
  }, [])

  const handleVerifyUser = React.useCallback((item: TraineesRowModel) => {
    setClickedRow(item)
    setShowVerifyDialog(true)
  }, [])

  const confirmDisconnect = React.useCallback(async () => {
    if (disconnectMany ? selectedIds.length === 0 : !selectedItem?.id) {
      return
    }

    setErrorMessage('')
    setIsDisconnectLoading(true)

    try {
      if (disconnectMany) {
        await disconnectTrainees({
          variables: {
            traineeIds: selectedIds as string[]
          }
        })
        setSuccessMessage(t('trainees.disconnectManySuccess'))
      } else {
        await disconnectTrainee({
          variables: {
            traineeId: selectedItem?.id as string
          }
        })
        setSuccessMessage(t('trainees.disconnectSuccess'))
      }

      updateSelectedIds([])
      refetch()
      if (deleteCallbackRef.current) {
        deleteCallbackRef.current()

        deleteCallbackRef.current = undefined
      }
    } catch (error) {
      setMessageByCode(error)
    } finally {
      setShowToast(true)
      setShowDisconnectDialog(false)
      setIsDisconnectLoading(false)
    }
  }, [selectedIds, selectedItem, disconnectMany])

  const confirmVerifyUser = React.useCallback(async () => {
    setIsVerifyLoading(true)
    setErrorMessage('')

    try {
      await confirmTrainee({
        variables: {
          id: selectedItem?.id as string
        }
      })

      setSuccessMessage(t('trainees.verifySuccess'))
    } catch (error) {
      setMessageByCode(error)
    } finally {
      setIsVerifyLoading(false)
      setShowToast(true)
      setShowVerifyDialog(false)
    }
  }, [selectedItem])

  const traineeData = React.useMemo(() => {
    if (isTrainingCompany) {
      const trainee = data?.trainingCompanies?.flatMap((trainingCompany) => {
        return trainingCompany.trainees.edges?.map(({ node }) => ({
          ...node,
          name: trainingCompany.name
        })) ?? []
      })

      return trainee || []
    } else {
      const trainee = traineesData?.me?.trainees?.map((user) => ({
        ...user,
        name: user.trainingCompanies?.[0]?.name
      }))

      return trainee || []
    }
  }, [data, traineesData])

  // Same caveat as the dashboard cards: every training company's trainees(...)
  // connection in this response shares the same skip/first, so the row count
  // is a sum across companies rather than one globally contiguous total.
  const rowCount = React.useMemo(() => {
    if (isTrainingCompany) {
      return data?.trainingCompanies?.reduce((sum, trainingCompany) => sum + trainingCompany.trainees.totalCount, 0) ?? 0
    }

    return traineesData?.me?.trainees?.length ?? 0
  }, [data, traineesData, isTrainingCompany])

  const updatePaginationModel = React.useCallback((model: GridPaginationModel) => {
    setPaginationModelState(model, data?.trainingCompanies?.[0]?.trainees.pageInfo.startCursor, data?.trainingCompanies?.[0]?.trainees.pageInfo.endCursor)
    handleFetchMore()
  }, [data])

  const updateSortModel = React.useCallback((model: GridSortModel) => {
    setSortModelState(model)
    handleFetchMore()
  }, [])

  const updateFilterModel = React.useCallback((filter: GridFilterModel | undefined) => {
    // Keep the field names the grid's own columns use (e.g. "profession")
    // in the state that feeds back into the controlled grid - otherwise
    // MUI can't match the filter row to a column and the value select
    // renders empty. Only remap field names for the outgoing API request.
    setFilterModelState(filter)

    if (fetchVariablesRef.current?.filter) {
      fetchVariablesRef.current.filter = fetchVariablesRef.current.filter.map((item: { field: string }) => ({
        ...item,
        field: GRID_FIELD_TO_API_FIELD[item.field] ?? item.field
      }))
    }

    handleFetchMore()
  }, [])

  const updateInitialState = React.useCallback((initialState?: GridInitialState) => {
    setInitialState(initialState)
    handleFetchMore()
  }, [])

  const handleInviteUser = React.useCallback(async (input: InviteTraineeInput, callback?: () => void) => {
    setErrorMessage('')

    try {
      await inviteTrainee({
        variables: {
          data: input
        }
      })

      setSuccessMessage(t('trainees.inviteSuccess'))
      callback?.()
      refetch()
    } catch (error: any) {
      if (error.message.includes('missingName') && !newTrainee) {
        setErrorMessage(t('errors.notRegistered'))
        setNewTrainee(true)
      } else if (error.message.includes('userNotTrainee') ||
      error.message.includes('userAlreadyInTrainingCompany') ||
      error.message.includes('missingName')) {
        setErrorMessage(t(error.message))
        setShowToast(true)
      } else {
        setMessageByCode(error)
        setShowToast(true)
      }
    } finally {
      setShowToast(true)
    }
  }, [])

  const updateNewTrainee = React.useCallback((value: boolean) => {
    setNewTrainee(value)
  }, [])

  const value = React.useMemo<TraineesProviderContextType>(() => ({
    traineeData,
    selectedIds,
    selectedItem,
    loading,
    newTrainee,
    paginationMode: isTrainingCompany ? 'server' : 'client',
    paginationModel: isTrainingCompany ? paginationModel : undefined,
    sortModel: isTrainingCompany ? (sortModel ? [sortModel] : []) : undefined,
    filterModel: isTrainingCompany ? filterModel : undefined,
    rowCount,
    updatePaginationModel,
    updateSortModel,
    updateFilterModel,
    updateInitialState,
    handleInviteUser,
    handleVerifyUser,
    updateNewTrainee,
    handleDisconnect,
    updateSelectedItem,
    updateSelectedIds
  }), [traineeData, loading, newTrainee, selectedIds, selectedItem, isTrainingCompany, paginationModel, sortModel, filterModel, rowCount])

  return (
    <TraineesProviderContext.Provider value={value}>
      {children}
      <ConfirmDialog
        title={t('common.disconnectEntry')}
        onCancel={() => setShowDisconnectDialog(false)}
        onConfirm={confirmDisconnect}
        loading={isDisconnectLoading}
        open={showDisconnectDialog}
      >
        <Trans
          i18nKey={disconnectMany ? 'trainees.disconnectEntries' : 'trainees.disconnectEntry'}
          {...(!disconnectMany ? { values: { name: selectedItem?.email } } : { count: selectedIds.length })}
          components={{ b: <strong /> }}
        />
      </ConfirmDialog>

      <ConfirmDialog
        title={t('trainees.verifyUser')}
        onCancel={() => setShowVerifyDialog(false)}
        onConfirm={confirmVerifyUser}
        loading={isVerifyLoading}
        open={showVerifyDialog}
      >
        <Trans
          i18nKey={'trainees.verifyUserDescription'}
          values={{ name: t('common.fullName', { firstName: selectedItem?.firstname, lastName: selectedItem?.lastname }) }}
          components={{ b: <strong /> }}
        />
      </ConfirmDialog>

      <DefaultSnackbar
        open={showToast}
        severity={errorMessage ? 'error' : 'success'}
        onClose={() => setShowToast(false)}
        message={errorMessage || successMessage}
      />
    </TraineesProviderContext.Provider>
  )
}

export const useTraineesContext = () => React.useContext(TraineesProviderContext)
